/**
 * Shared SSE line helpers (no browser / auth dependencies).
 */

/** Return JSON payload for an SSE line, or null for blank/comment keep-alives. */
export function sseEventPayload(line) {
  const trimmed = String(line ?? '').trim();
  if (!trimmed) return null;
  if (trimmed.startsWith(':')) return null; // e.g. CRM Backend `:ok`
  return trimmed.startsWith('data: ') ? trimmed.slice(6) : trimmed;
}

export function createStreamState() {
  return { content: '', parts: [], references: [] };
}

export function snapshotStreamState(state) {
  return {
    content: state.content,
    parts: [...state.parts],
    references: [...state.references],
  };
}

function appendText(state, chunk) {
  state.content += chunk;
  const last = state.parts[state.parts.length - 1];
  if (last && last.kind === 'text') {
    last.content += chunk;
  } else {
    state.parts.push({ kind: 'text', content: chunk });
  }
}

/**
 * Apply one parsed SSE event to the streaming reducer.
 * @returns {{ kind: 'update'|'complete'|'error'|'ignore', result?: object, error?: object }}
 */
export function applySseEvent(state, event) {
  if (!event || typeof event.type !== 'string') return { kind: 'ignore' };

  if (event.type === 'text_delta' && event.content) {
    appendText(state, event.content);
    return { kind: 'update' };
  }

  if (event.type === 'references' && event.references) {
    state.references = event.references;
    return { kind: 'update' };
  }

  if (event.type === 'block' && event.block) {
    state.parts.push({ kind: 'block', block: event.block });
    return { kind: 'update' };
  }

  if (event.type === 'status' && event.id) {
    const existingIndex = state.parts.findIndex(
      (p) => p.kind === 'status' && p.status?.id === event.id
    );
    if (existingIndex >= 0) {
      const existing = state.parts[existingIndex];
      state.parts[existingIndex] = {
        kind: 'status',
        status: {
          ...existing.status,
          ...(event.label !== undefined && { label: event.label }),
          ...(event.state !== undefined && { state: event.state }),
          ...(event.result !== undefined && { result: event.result }),
          ...(event.kind !== undefined && { kind: event.kind }),
        },
      };
    } else {
      state.parts.push({
        kind: 'status',
        status: {
          id: event.id,
          label: event.label ?? '',
          state: event.state ?? 'in_progress',
          ...(event.result !== undefined && { result: event.result }),
          ...(event.kind !== undefined && { kind: event.kind }),
        },
      });
    }
    return { kind: 'update' };
  }

  if (event.type === 'error') {
    return {
      kind: 'error',
      error: {
        code: event.code,
        message: event.message ?? 'AI Assistant stream failed.',
        retriable: event.retriable,
      },
    };
  }

  if (event.type === 'complete') {
    return {
      kind: 'complete',
      result: {
        conversationId: event.conversationId,
        userMessageId: event.userMessageId,
        assistantMessageId: event.assistantMessageId,
        answer: state.content,
        parts: [...state.parts],
        references: [...state.references],
        metadata: event.metadata,
      },
    };
  }

  // Unknown event types (including `final`) are ignored per ADR 0003.
  return { kind: 'ignore' };
}

export function streamIncompleteError() {
  return {
    code: 'stream_incomplete',
    message: 'AI Assistant did not finish the response. Please try again.',
    retriable: true,
  };
}
